import { useState } from 'react';
import { ApolloQueryResult, QueryResult } from '@apollo/client';

import {
  usePointListQuery,
  PointQuerySort,
  PointQueryFilter,
  PointQueryPagination,
  PointPureType,
  PlayerDevicePureType,
  PaginationInfoType,
  PointsPlayingStatusesPureType,
  PointShortTypeFragment,
  PointListQuery,
  InputMaybe,
  Maybe,
} from '@shared/api/graphql/generated';
import { FetchPolicyKeys, BaseQueryRefetchResultType } from '@shared/api/graphql/types';
import { LicenseType } from '@entities/billing';
import { StreamType } from '@entities/media';

export type PointListQueryState = {
  query?: string | null;
  companyIds: InputMaybe<string>[];
  companyTagIds: InputMaybe<string>[];
  playingStatuses: InputMaybe<string>[];
  isVideoDemo?: boolean | null;
  page: number;
  pageSize: number;
  sort: PointQuerySort;
  skip: boolean;
  hasVideoSupport?: InputMaybe<boolean>;
};

export type PointPreviewType = Pick<PointPureType, 'id' | 'comment' | 'address' | 'playingStatus' | 'isEnabled'> & {
  stream?: Maybe<Pick<StreamType, 'id' | 'title'>>;
  license?: Maybe<LicenseType>;
  device?: Maybe<Pick<PlayerDevicePureType, 'id' | 'requiredVersionUrl' | 'type' | 'bindingCode'>>;
};

export type UsePointListResultType = BaseQueryRefetchResultType & {
  called: boolean;
  data: {
    points?: PointShortTypeFragment[] | null;
    statusesInfo?: PointsPlayingStatusesPureType | null;
    paginationInfo?: PaginationInfoType;
  };
  loadMore: (
    filters: PointQueryFilter,
    pagination: PointQueryPagination,
    sort: PointQuerySort
  ) => Promise<ApolloQueryResult<PointListQuery>>;
  fetchMore: QueryResult['fetchMore'];
};

export type UsePointsType = [
  request: (filters: PointQueryFilter, pagination: PointQueryPagination, sort: PointQuerySort) => void,
  result: UsePointListResultType,
];

const usePointList = (): UsePointsType => {
  const [queryState, setQueryState] = useState<PointListQueryState>({
    query: '',
    companyIds: [],
    companyTagIds: [],
    playingStatuses: [],
    page: 0,
    pageSize: 0,
    sort: PointQuerySort.IdDesc,
    skip: true,
  });

  const { data, called, loading, networkStatus, fetchMore, refetch } = usePointListQuery({
    variables: {
      filters: {
        query: queryState.query,
        companyIds: queryState.companyIds,
        companyTagIds: queryState.companyTagIds as string[],
        playingStatuses: queryState.playingStatuses,
        isVideoDemo: queryState.isVideoDemo,
        hasVideoSupport: queryState.hasVideoSupport,
      },
      pagination: { page: queryState.page, pageSize: queryState.pageSize },
      sort: queryState.sort,
    },
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    notifyOnNetworkStatusChange: true,
    skip: queryState.skip,
  });

  const request = (filters: PointQueryFilter, pagination: PointQueryPagination, sort: PointQuerySort) => {
    setQueryState({
      query: filters.query,
      companyIds: filters.companyIds || [],
      companyTagIds: filters.companyTagIds || [],
      playingStatuses: filters.playingStatuses || [],
      isVideoDemo: filters.isVideoDemo,
      page: pagination.page,
      hasVideoSupport: filters.hasVideoSupport,
      pageSize: pagination.pageSize,
      sort,
      skip: false,
    });
  };

  async function loadMore(filters: PointQueryFilter, pagination: PointQueryPagination, sort: PointQuerySort) {
    const result = await fetchMore({
      variables: {
        filters,
        pagination,
        sort,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        const data = {
          ...prev,
          pointsPaginationQuery: {
            ...prev?.pointsPaginationQuery,
            ...fetchMoreResult?.pointsPaginationQuery,
            result: [
              ...(prev?.pointsPaginationQuery?.result || []),
              ...(fetchMoreResult?.pointsPaginationQuery.result || []),
            ],
          },
        };

        return data;
      },
    });

    return result;
  }

  const result: UsePointListResultType = {
    loading,
    called,
    networkStatus,
    data: {
      points: data?.pointsPaginationQuery.result,
      statusesInfo: data?.pointsPaginationQuery.statusesInfo,
      paginationInfo: data?.pointsPaginationQuery.paginationInfo,
    },
    loadMore,
    refetch,
    fetchMore,
  };

  return [request, result];
};

export default usePointList;
